import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { QuestionAnswer } from '@revelio/core';
import type { CompositionPageScope } from '@revelio/core/global-ai-assistant/composition-page-scope';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { openReplayStateLogger } from '../../hooks/open-replay';
import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantConversationTurn,
  GlobalAssistantEntityType,
} from './global-ai-assistant-api';
import type { LastAppliedFilterContext } from './global-ai-assistant-discovery-followup';
import type { GlobalAssistantResolvedRouteFilters } from './global-ai-assistant-route-filter-kind';
import type { PageGenProgressEvent } from './global-ai-page-gen-progress';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import type { ChartDataDigest } from './turn-outcome-digest';
import type { AssistantTaxonomyPayload } from './use-global-ai-assistant-taxonomy';

/**
 * Zustand queue for drawer -> page command flow.
 *
 * Problem this solves:
 * The drawer (global, outside CustomDash) needs to trigger work that only
 * makes sense inside CustomDash (dispatch, filter apply, stream gen). The
 * drawer typically emits RIGHT BEFORE `navigate()` — so the bridge that
 * should handle the command isn't mounted yet (old bridge is, for the
 * outgoing page). A synchronous "emit = run handler now" design loses
 * those commands.
 *
 * How this works:
 * - Commands are appended to a pending queue by `emit`.
 * - The bridge (inside CustomDash) subscribes via `useGlobalAiCommandStore`
 *   and drains the queue in a `useEffect`: for each command whose
 *   `entityType` matches the current page and (for route-filter commands)
 *   whose `targetPath` matches the current pathname, it consumes the
 *   command and runs its handler.
 * - Commands that don't match the current bridge stay in the queue until
 *   either a matching bridge mounts or they expire.
 * - `requestId` dedup prevents React StrictMode double-invoke from
 *   processing the same command twice.
 *
 * Alongside the queue the store also holds `activeTaxonomyByEntity` —
 * bridges publish their page's active taxonomy here so the drawer (which
 * has no filter context of its own) can use the page-level taxonomy.
 */

export const COMMAND_TTL_MS = 30_000;

export type GlobalAiCommand =
  | ({
      kind: 'applyRouteFilters';
      requestId: string;
      /**
       * Pre-resolved filters (`filterKind` + `response`). The drawer calls
       * the matching AI endpoint (TD v2 or company-screener) before emitting.
       */
      targetPath: string;
      entityType: GlobalAssistantEntityType;
      prompt?: string;
      merge?: boolean;
      /**
       * Taxonomy pairing for the destination page. DEFAULT (unset) = the
       * bridge forwards `pairRoleTaxonomyWithResponse: true` to
       * `applyAiSearchFilters`, so the page ADOPTS the taxonomy the
       * response's role/skill ids were resolved under (`roles_tax_id` /
       * `skills_tax_id`) — ids are taxonomy-relative, and labeling or
       * querying them under any other taxonomy is wrong (the
       * "engineer id labeled Customer Operations" chip bug). Pass `false`
       * ONLY for an apply whose ids are known NOT to come from the AI
       * response. The paired filter lands `isAiApplied`, which pref-sync
       * and the adaptive hooks already honor and which a user taxonomy
       * change wholesale-replaces (releasing the pin).
       */
      pairTaxonomy?: boolean;
      /**
       * Set only by the geography-taxonomy switch, to the taxonomy it chose.
       * The page renders geography from `FilterId.GEOGRAPHY_TAXONOMY`, and the
       * switch writes the preference without dispatching that filter — so the
       * apply has to re-key it, and can only do so if told which taxonomy was
       * chosen.
       */
      adoptGeoTax?: number;
    } & GlobalAssistantResolvedRouteFilters)
  | {
      kind: 'applySingleChart';
      requestId: string;
      config: LLMTabContentForDispatch;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      prompt?: string;
      entityType: CustomDashAssistantEntityType;
      targetPath: string;
    }
  | ({
      kind: 'startCustomPageStream';
      requestId: string;
      prompt: string;
      model?: string;
      entityType: CustomDashAssistantEntityType;
      answers?: QuestionAnswer[];
      /**
       * Bounded conversation context (recent turns + optional summary
       * of older turns). The bridge forwards this to the page-stream
       * backend so multi-turn intent and entity context survive
       * "Create custom page" / "Update current page" clicks.
       */
      conversation?: {
        turns: GlobalAssistantConversationTurn[];
        summary?: string;
      };
    } & (
      | {
          /**
           * Generates a fresh page layout from scratch and is cacheable.
           * The target path keeps the outgoing edit page from claiming work
           * intended for the newly opened draft.
           */
          mode?: 'create';
          targetPath: string;
        }
      | {
          /**
           * Patches the current page's layout in-place. The current tab
           * content IS the state to update, so there's no useful cache
           * key. Conversation context above carries pronoun and intent
           * history.
           */
          mode: 'update';
        }
    ));

export function changesPageScope(command: GlobalAiCommand): boolean {
  return (
    command.kind === 'applyRouteFilters' || command.kind === 'applySingleChart'
  );
}

type QueuedCommand = {
  command: GlobalAiCommand;
  queuedAtMs: number;
  status: 'queued' | 'in_flight';
  /**
   * Pathname recorded by `claim()` when a bridge starts handling the
   * command. Used by `selectActiveCustomPageStreamForPath` to scope
   * update-mode streams (which have no `targetPath`) to the page that
   * actually owns the work, so navigating to a different custom dash
   * mid-stream doesn't surface a misleading loading state there.
   */
  claimedAtPath?: string;
};

/**
 * True when the queue contains a `startCustomPageStream` command (queued
 * or in flight) that is bound to `pathname`. Used by the CustomDash page
 * to mirror the chat's loading state on the page itself while the AI is
 * still building out the layout.
 *
 * - `mode: 'create'` commands carry an explicit `targetPath` — match on it.
 * - `mode: 'update'` commands have no target path; they're scoped via the
 *   `claimedAtPath` the owning bridge recorded when it called `claim()`.
 *   A queued (unclaimed) update only matches if no other bridge has
 *   claimed it yet AND the caller is on a CustomDash edit route — the
 *   bridge's own filter does that — so we only consider `in_flight`
 *   updates here and compare against `claimedAtPath`.
 */
export function selectActiveCustomPageStreamForPath(
  queue: readonly QueuedCommand[],
  pathname: string
): QueuedCommand | null {
  for (const entry of queue) {
    if (entry.command.kind !== 'startCustomPageStream') {
      continue;
    }
    const command = entry.command;
    if (command.mode === 'update') {
      if (
        entry.status === 'in_flight' &&
        entry.claimedAtPath !== undefined &&
        globalAssistantPathsMatch(pathname, entry.claimedAtPath)
      ) {
        return entry;
      }
      continue;
    }
    if (globalAssistantPathsMatch(pathname, command.targetPath)) {
      return entry;
    }
  }
  return null;
}

/**
 * One published occurrence on an event channel. `seq` is monotonic per
 * channel; consumers remember the last seq they handled and read forward.
 * `at` is the publish wall-clock, for consumers that anchor events to turn
 * timing (e.g. "does this adjustment predate the current turn?").
 */
export type ChannelEvent<T> = {
  seq: number;
  at: number;
  event: T;
};

/**
 * A consumable event channel: a bounded, newest-last window of recent events
 * plus the seq of the newest one (`seq` is 0 when nothing was ever published).
 *
 * WHY this shape and not a "latest value + timestamp" slot: a slot coalesces —
 * two publishes inside one render window leave only the second observable, so
 * consumers silently drop events (two filter-apply failures in one prune pass
 * used to surface as one). Keeping a window makes every publish observable;
 * consumption is per-consumer (each tracks its own seq via
 * `channelEventsSince`), never destructive across consumers.
 */
export type EventChannel<T> = {
  seq: number;
  events: readonly ChannelEvent<T>[];
};

/**
 * Payload per channel. All four are genuine event streams (each occurrence
 * matters); `lastAppliedFilterContextByPath` deliberately stays OUT of this
 * map — its consumer wants "the latest applied context for this path", a
 * keyed latest-value, so a slot map is the honest shape for it.
 */
export type GlobalAiChannelPayloads = {
  /**
   * A filter apply the chat already promised ("applying filters now…")
   * definitively failed — the queued command aged out unconsumed
   * (`reason: 'expired'`, from `pruneExpired`) or a bridge consumed it and
   * the apply threw (`reason: 'error'`). `paths` covers every apply that
   * failed in the same event (one prune pass can expire several at once).
   */
  filterApplyFailure: {
    requestId: string;
    paths: string[];
    reason: 'expired' | 'error';
  };
  /**
   * An apply SUCCEEDED but the destination page adjusted it — today,
   * selections its cap dropped. Distinct from `filterApplyFailure`: the
   * filters did land, so this corrects what the chat already claimed rather
   * than reporting an error.
   */
  filterApplyAdjustment: {
    requestId: string;
    path: string;
    droppedNames: string[];
  };
  /**
   * Widget digests collected when a custom page finished generating,
   * published by the bridge's page-narration collector. The turn runner
   * awaits the first post-baseline event so the model narrates the page's
   * ACTUAL widgets. `expectedCount` is how many widgets the collector was
   * armed for (digests may be fewer).
   */
  pageCollection: {
    digests: ChartDataDigest[];
    prompt: string;
    expectedCount: number;
  };
  /**
   * Structured generation-progress for an in-flight custom-page build,
   * published by the bridge as the page-stream emits. The turn runner
   * subscribes and routes each event to the activity lane.
   */
  pageGenProgress: PageGenProgressEvent;
};

export type GlobalAiChannelName = keyof GlobalAiChannelPayloads;

export type GlobalAiEventChannels = {
  [K in GlobalAiChannelName]: EventChannel<GlobalAiChannelPayloads[K]>;
};

/**
 * Per-channel buffer bound. Consumers read events within the same render
 * window (engine effects) or synchronously per publish (runner
 * subscriptions), so the window only needs to absorb a same-tick burst; 8 is
 * far beyond any real burst while keeping the store (and its OpenReplay
 * state log) small. A consumer further behind than the bound misses only the
 * overwritten oldest events — bounded loss.
 */
const EVENT_CHANNEL_BUFFER = 8;

/**
 * Fresh, empty channel set — the store's initial value. Exported for specs
 * that reset the channels between cases (the engine-core spec's lifecycle and
 * drain suites); app code only ever reads channels off the store.
 */
export function createEmptyEventChannels(): GlobalAiEventChannels {
  return {
    filterApplyFailure: { seq: 0, events: [] },
    filterApplyAdjustment: { seq: 0, events: [] },
    pageCollection: { seq: 0, events: [] },
    pageGenProgress: { seq: 0, events: [] },
  };
}

/**
 * Pure append: next channels object with `event` published on `channel`.
 * Shared by the `publishChannelEvent` action and `pruneExpired` (which folds
 * its expiry publish into the same `set` that drops the expired commands).
 */
function withChannelEvent<K extends GlobalAiChannelName>(
  channels: GlobalAiEventChannels,
  channel: K,
  event: GlobalAiChannelPayloads[K],
  at: number
): GlobalAiEventChannels {
  const current = channels[channel];
  const seq = current.seq + 1;
  const events = [...current.events, { seq, at, event }].slice(
    -EVENT_CHANNEL_BUFFER
  );
  return { ...channels, [channel]: { seq, events } };
}

/**
 * Events on `channel` a consumer at watermark `sinceSeq` has not handled yet,
 * oldest first. Callers advance their watermark to each event's `seq` as they
 * handle it.
 */
export function channelEventsSince<T>(
  channel: EventChannel<T>,
  sinceSeq: number
): readonly ChannelEvent<T>[] {
  if (channel.seq <= sinceSeq) {
    return [];
  }
  return channel.events.filter((e) => e.seq > sinceSeq);
}

type GlobalAiCommandState = {
  /** Ordered FIFO of commands awaiting or mid-handling. Newest at tail. */
  queue: readonly QueuedCommand[];

  /**
   * Consumable event channels — see `EventChannel` for the contract. The
   * bridge/store publish; the engine and turn runner consume via
   * `channelEventsSince` (per-consumer seq watermarks) or
   * `waitForChannelEvent` (one-shot waits).
   */
  channels: GlobalAiEventChannels;

  /** Publish `event` on `channel` (monotonic per-channel `seq`). */
  publishChannelEvent: <K extends GlobalAiChannelName>(
    channel: K,
    event: GlobalAiChannelPayloads[K]
  ) => void;

  /**
   * True while any command is mid-flight. Drawer reads this to disable
   * submit buttons.
   */
  isCommandInFlight: boolean;

  /**
   * Current CustomDash page's taxonomy, published by the mounted bridge.
   * Keyed by entity type. Undefined for an entity means no bridge has
   * published for it in this session — the drawer falls back to saved
   * user preferences in that case.
   *
   * TODO(backend-adaptive): re-evaluate when the backend accepts
   * "adaptive". The primary consumer is `waitForTaxonomyChange` (used by
   * the drawer's refinement pass for adaptive-company). Once the
   * refinement goes away, the only remaining value is picking up session
   * overrides from the Preferences modal that haven't been "Set as
   * default" — niche; consider removing the whole
   * `activeTaxonomyByEntity` + `publishActiveTaxonomy` pair and letting
   * the drawer read saved prefs directly.
   */
  activeTaxonomyByEntity: Partial<
    Record<GlobalAssistantEntityType, AssistantTaxonomyPayload>
  >;

  /**
   * Last successfully applied global-assistant filter prompt per path.
   * Used to interpret filter refinements on the same page.
   */
  lastAppliedFilterContextByPath: Record<string, LastAppliedFilterContext>;

  /**
   * The mounted bridge's page filters, in the resolver's artifact shape,
   * keyed by pathname. The engine sends these with each turn so a
   * page-relative ask ("how does that look for engineers?") reads the page's
   * companies and filters underneath it rather than the whole market.
   *
   * Keyed by PATH, not entity: two pages of the same entity type carry
   * different filters, and a stale entry from the page the user left would
   * scope the answer to somewhere they are no longer looking. The bridge
   * clears its own path on unmount.
   */
  currentPageFiltersByPath: Record<string, CompositionPageScope>;

  /**
   * Bridge-side report of a CONSUMED apply command whose apply threw —
   * published on the `filterApplyFailure` channel. Complements pruneExpired's
   * TTL detection so the in-chat failure surface (DP4 optimistic
   * apply_filters: the chat already promised "applying filters now…") covers
   * both ways an optimistic apply can die.
   */
  reportFilterApplyFailure: (input: {
    requestId: string;
    path: string;
  }) => void;

  /**
   * Bridge-side report of a consumed apply the page adjusted — published on
   * the `filterApplyAdjustment` channel. `apply_filters` narrates
   * optimistically from the artifact (DP4), before the page has capped
   * anything, so without this the chat asserts every requested entity
   * applied while the toast says otherwise.
   */
  reportFilterApplyAdjustment: (input: {
    requestId: string;
    path: string;
    droppedNames: string[];
  }) => void;

  /**
   * Bridge calls this on mount and whenever its activeFilterMap changes.
   * Pass `undefined` for `taxonomy` on unmount to clear the published value.
   *
   * TODO(backend-adaptive): see `activeTaxonomyByEntity` above.
   */
  publishActiveTaxonomy: (
    entityType: GlobalAssistantEntityType,
    taxonomy: AssistantTaxonomyPayload | undefined
  ) => void;

  publishLastAppliedFilterContext: (
    targetPath: string,
    context: LastAppliedFilterContext
  ) => void;

  /**
   * Bridge calls this on mount and whenever its filters change. Pass
   * `undefined` on unmount to clear the path — a page's filters must not
   * outlive the page.
   */
  publishCurrentPageFilters: (
    pathname: string,
    filters: CompositionPageScope | undefined
  ) => void;

  /** Append a command to the queue. Bridges consume it when they match. */
  emit: (command: GlobalAiCommand) => void;

  /**
   * Mark a queued command as `in_flight`. Returns true if the caller
   * should proceed (i.e. the command was previously 'queued' and this
   * caller is now the owner). Returns false if someone else already
   * claimed it (StrictMode double-invoke guard). The optional
   * `claimedAtPath` records which CustomDash route is handling the
   * command — used by `selectActiveCustomPageStreamForPath` to scope
   * page-local loading UI for update-mode streams.
   */
  claim: (requestId: string, claimedAtPath?: string) => boolean;

  /**
   * Mark a command handled and remove it from the queue.
   *
   * "Completed" means a bridge consumed the command and finished handling it —
   * NOT that the work succeeded. The bridge calls this from a `finally`, so a
   * handler that threw still completes; success/failure is reported separately
   * (`reportFilterApplyFailure`, the collected widget digests).
   *
   * MUST only be called after a successful `claim`. `waitForCommandComplete`
   * distinguishes a handled command from one that expired unclaimed purely by
   * whether the entry ever reached `in_flight`, so completing an unclaimed
   * command would read as an expiry. The bridge's single call site upholds
   * this: its `finally` is inside a branch gated on `claim` returning true.
   */
  complete: (requestId: string) => void;

  /**
   * Drop a still-QUEUED command that its emitter no longer wants — the turn
   * that emitted it was aborted before any bridge picked it up.
   *
   * Without this, an aborted turn's command stays claimable for the rest of
   * its TTL: the user hits Stop, then the destination page's bridge mounts a
   * couple of seconds later, `pruneExpired` keeps the still-fresh entry, and
   * the build the user just cancelled runs anyway.
   *
   * Deliberately a NO-OP on `in_flight` entries. A bridge that already started
   * handling the command owns its own teardown, and silently removing the
   * entry underneath it would make `waitForCommandComplete` read the removal
   * as an unclaimed expiry.
   */
  cancelIfUnclaimed: (requestId: string) => void;

  /** Remove expired queued commands (> TTL old). Called by the bridge. */
  pruneExpired: () => void;
};

export const useGlobalAiCommandStore = create<GlobalAiCommandState>()(
  devtools(
    openReplayStateLogger(
      (set, get) => ({
        queue: [],
        channels: createEmptyEventChannels(),
        isCommandInFlight: false,
        activeTaxonomyByEntity: {},
        lastAppliedFilterContextByPath: {},
        currentPageFiltersByPath: {},

        publishChannelEvent: (channel, event) => {
          set(
            {
              channels: withChannelEvent(
                get().channels,
                channel,
                event,
                Date.now()
              ),
            },
            false,
            `global-ai/publishChannelEvent:${channel}`
          );
        },

        reportFilterApplyAdjustment: ({ requestId, path, droppedNames }) => {
          // Nothing was dropped — there is no correction to surface.
          if (droppedNames.length === 0) return;
          set(
            {
              channels: withChannelEvent(
                get().channels,
                'filterApplyAdjustment',
                { requestId, path, droppedNames },
                Date.now()
              ),
            },
            false,
            'global-ai/reportFilterApplyAdjustment'
          );
        },

        reportFilterApplyFailure: ({ requestId, path }) => {
          set(
            {
              channels: withChannelEvent(
                get().channels,
                'filterApplyFailure',
                { requestId, paths: [path], reason: 'error' },
                Date.now()
              ),
            },
            false,
            'global-ai/reportFilterApplyFailure'
          );
        },

        publishActiveTaxonomy: (entityType, taxonomy) => {
          const current = get().activeTaxonomyByEntity;
          const existing = current[entityType];
          // Skip set if unchanged — avoids re-render churn when the bridge's
          // activeFilterMap updates with an unrelated filter change.
          if (taxonomy === undefined) {
            if (existing === undefined) return;
            const next = { ...current };
            delete next[entityType];
            set(
              { activeTaxonomyByEntity: next },
              false,
              `global-ai/clearTaxonomy:${entityType}`
            );
            return;
          }
          if (
            existing &&
            existing.rolesTax === taxonomy.rolesTax &&
            existing.skillsTax === taxonomy.skillsTax
          ) {
            return;
          }
          set(
            {
              activeTaxonomyByEntity: { ...current, [entityType]: taxonomy },
            },
            false,
            `global-ai/publishTaxonomy:${entityType}`
          );
        },

        publishCurrentPageFilters: (pathname, filters) => {
          const current = get().currentPageFiltersByPath;
          const existing = current[pathname];
          if (filters === undefined) {
            if (existing === undefined) return;
            const next = { ...current };
            delete next[pathname];
            set(
              { currentPageFiltersByPath: next },
              false,
              'global-ai/clearCurrentPageFilters'
            );
            return;
          }
          // Skip the set when nothing changed — the bridge republishes on
          // every `activeFilterMap` identity change, most of which touch a
          // filter this adapter does not carry. Compared by value (the
          // artifact is small and built key-for-key the same way each time)
          // because the adapter returns a fresh object every call, so
          // reference equality would never hold and every unrelated render
          // would churn the store and re-render its subscribers.
          if (
            existing &&
            JSON.stringify(existing) === JSON.stringify(filters)
          ) {
            return;
          }
          set(
            {
              currentPageFiltersByPath: { ...current, [pathname]: filters },
            },
            false,
            'global-ai/publishCurrentPageFilters'
          );
        },

        publishLastAppliedFilterContext: (targetPath, context) => {
          const trimmedPrompt = context.prompt.trim();
          if (!trimmedPrompt) {
            return;
          }
          const current = get().lastAppliedFilterContextByPath;
          const existing = current[targetPath];
          if (
            existing &&
            existing.surfaceKind === context.surfaceKind &&
            existing.discoveryKind === context.discoveryKind &&
            existing.entityType === context.entityType &&
            existing.prompt === trimmedPrompt
          ) {
            return;
          }
          set(
            {
              lastAppliedFilterContextByPath: {
                ...current,
                [targetPath]: {
                  targetPath,
                  surfaceKind: context.surfaceKind,
                  ...(context.discoveryKind
                    ? { discoveryKind: context.discoveryKind }
                    : {}),
                  ...(context.entityType
                    ? { entityType: context.entityType }
                    : {}),
                  prompt: trimmedPrompt,
                },
              },
            },
            false,
            `global-ai/publishFilterContext:${targetPath}`
          );
        },

        emit: (command) => {
          const state = get();
          // requestId dedupe — drop replays.
          if (
            state.queue.some((q) => q.command.requestId === command.requestId)
          ) {
            return;
          }
          set(
            {
              // Invalidate before React commits so later steps cannot read old filters.
              ...(changesPageScope(command)
                ? { currentPageFiltersByPath: {} }
                : {}),
              queue: [
                ...state.queue,
                { command, queuedAtMs: Date.now(), status: 'queued' },
              ],
            },
            false,
            `global-ai/emit:${command.kind}`
          );
        },

        claim: (requestId, claimedAtPath) => {
          const { queue } = get();
          const entry = queue.find((q) => q.command.requestId === requestId);
          if (!entry || entry.status !== 'queued') {
            return false;
          }
          set(
            {
              queue: queue.map((q) =>
                q.command.requestId === requestId
                  ? {
                      ...q,
                      status: 'in_flight',
                      ...(claimedAtPath !== undefined ? { claimedAtPath } : {}),
                    }
                  : q
              ),
              isCommandInFlight: true,
            },
            false,
            `global-ai/claim:${entry.command.kind}`
          );
          return true;
        },

        complete: (requestId) => {
          const { queue } = get();
          const next = queue.filter((q) => q.command.requestId !== requestId);
          set(
            {
              queue: next,
              isCommandInFlight: next.some((q) => q.status === 'in_flight'),
            },
            false,
            'global-ai/complete'
          );
        },

        cancelIfUnclaimed: (requestId) => {
          const { queue } = get();
          const entry = queue.find((q) => q.command.requestId === requestId);
          if (!entry || entry.status !== 'queued') {
            return;
          }
          set(
            { queue: queue.filter((q) => q.command.requestId !== requestId) },
            false,
            `global-ai/cancelIfUnclaimed:${entry.command.kind}`
          );
        },

        pruneExpired: () => {
          const now = Date.now();
          const { queue } = get();
          const isFresh = (q: (typeof queue)[number]) =>
            q.status === 'in_flight' || now - q.queuedAtMs < COMMAND_TTL_MS;
          const next = queue.filter(isFresh);
          if (next.length === queue.length) {
            return;
          }
          // Expired filter-apply commands mean the user was told filters were
          // being applied but no page ever consumed them — record ALL of this
          // pass's expiries so the drawer can surface the failure in-chat
          // (DP4). Covers both apply shapes the chat promises: direct route
          // applies AND discovery search/applies (whose bridge may never
          // mount if the navigation was blocked). One prune pass can expire
          // several at once.
          const expiredPaths: string[] = [];
          let expiredRequestId: string | undefined;
          for (const q of queue) {
            if (!isFresh(q) && q.command.kind === 'applyRouteFilters') {
              expiredPaths.push(q.command.targetPath);
              expiredRequestId ??= q.command.requestId;
            }
          }
          set(
            {
              queue: next,
              // Fold the failure publish into the SAME set as the queue drop
              // so a subscriber can never observe pruned commands without the
              // failure event (or vice versa).
              ...(expiredRequestId !== undefined
                ? {
                    channels: withChannelEvent(
                      get().channels,
                      'filterApplyFailure',
                      {
                        requestId: expiredRequestId,
                        paths: expiredPaths,
                        reason: 'expired' as const,
                      },
                      now
                    ),
                  }
                : {}),
            },
            false,
            'global-ai/pruneExpired'
          );
        },
      }),
      'global_ai_command'
    ),
    { name: 'GlobalAiCommand' }
  )
);

/**
 * `applyRouteFilters.pairTaxonomy` semantics: pairing is the default;
 * only an explicit `false` opts out. See the field docblock.
 */
export function shouldPairTaxonomyWithResponse(
  pairTaxonomy: boolean | undefined
): boolean {
  return pairTaxonomy !== false;
}

export type CommandCompletionOutcome =
  | 'completed'
  | 'expired'
  | 'timeout'
  | 'aborted';

/**
 * The outcome the page-build tools thread through to their model-facing result.
 * Derived from `CommandCompletionOutcome` rather than respelled, so renaming the
 * member can't silently desync the sites that narrate it.
 */
export type CommandExpiredOutcome = Extract<
  CommandCompletionOutcome,
  'expired'
>;

/**
 * Wait for a specific command to leave the queue and report WHY it left.
 *
 * Leaving the queue is not the same as succeeding. `pruneExpired` removes
 * commands too, so a bare "it's gone" made a build that never started look like
 * one that finished, and the model narrated a dashboard the user never got.
 *
 * The discriminator is CLAIM STATUS, not the removal path: `pruneExpired`'s
 * `isFresh` keeps every `in_flight` entry, so a command that never left
 * 'queued' is exactly one no bridge ever claimed. Watching the status directly
 * also covers the case that motivated this — nothing prunes on a timer
 * (`pruneExpired`'s only production caller is the bridge's drain effect), so
 * when no bridge ever mounts, the entry is still sitting in the queue when our
 * own deadline fires. That is an expiry, not a timeout: `'timeout'` is reserved
 * for a command a bridge DID claim but didn't finish in time.
 *
 * `'expired'` is only ever reported when it is PROVABLE — the command was
 * removed while still unclaimed (only `pruneExpired` can do that; `complete`
 * requires a claim), or it is still queued and already past `COMMAND_TTL_MS`.
 * A caller whose `timeoutMs` is shorter than the TTL gets `'timeout'` for a
 * command that is merely unclaimed *so far*, because such a command can still
 * be picked up and run. Do NOT reintroduce a bare `everClaimed` check here:
 * the outcome feeds copy that tells the user, definitively, that their build
 * never started.
 *
 * Callers must branch on `'completed'` before treating the work as done.
 * Callers: `use-global-ai-turn-runner.ts` (both page-build paths) and
 * `assistant-engine-core.ts` (gates the adaptive-taxonomy refinement on the
 * initial apply landing).
 *
 * TODO(backend-adaptive): the engine's refinement caller goes away with the
 * refinement pass once the AI filter search endpoint accepts "adaptive" as a
 * taxonomy value; the turn-runner callers stay.
 */
export function waitForCommandComplete(
  requestId: string,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<CommandCompletionOutcome> {
  const findEntry = (queue: readonly QueuedCommand[]) =>
    queue.find((q) => q.command.requestId === requestId);

  if (signal?.aborted) return Promise.resolve('aborted');

  const entry = findEntry(useGlobalAiCommandStore.getState().queue);
  // Not queued at all. Not reachable today — every caller emits with a fresh
  // `crypto.randomUUID()` synchronously right before awaiting — but `emit`'s
  // requestId dedupe drops a replayed id silently, and an emit that ever
  // becomes deferred would land here too. We have observed NOTHING about this
  // command, so report the conservative outcome, not the optimistic one:
  // inferring success from absence is the exact bug this function exists to
  // remove. A spurious "try again" costs the user a retry; a spurious
  // "completed" makes the model narrate a dashboard nobody built.
  if (!entry) return Promise.resolve('expired');

  let everClaimed = entry.status === 'in_flight';

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      cleanup();
      // At the deadline the command is still queued, so "expired" has to be
      // PROVEN rather than assumed from the caller's timeout. Read it off the
      // command's own age: only past COMMAND_TTL_MS is it truly unclaimable
      // (the next `pruneExpired` will drop it). A caller whose wait is shorter
      // than the TTL — the engine's refinement gate passes 10s — would
      // otherwise report a still-live command as "never started", and
      // `create_custom_page` narrates that as a definite "the build did NOT
      // start" moments before a late-mounting bridge claims and builds it.
      if (everClaimed) return resolve('timeout');
      const ageMs = Date.now() - entry.queuedAtMs;
      resolve(ageMs >= COMMAND_TTL_MS ? 'expired' : 'timeout');
    }, timeoutMs);
    const onAbort = () => {
      cleanup();
      resolve('aborted');
    };
    const cleanup = () => {
      clearTimeout(timer);
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort);
    const unsubscribe = useGlobalAiCommandStore.subscribe((state) => {
      const current = findEntry(state.queue);
      if (current) {
        // `claim` and `complete` are separate `set` calls and zustand notifies
        // synchronously per set, so the in_flight state is always observed
        // before the removal.
        if (current.status === 'in_flight') everClaimed = true;
        return;
      }
      cleanup();
      resolve(everClaimed ? 'completed' : 'expired');
    });
  });
}

/**
 * Wait for the FIRST event on `channel` published after `sinceSeq`.
 *
 * Mirrors `waitForCommandComplete`'s subscribe+timer+abort idiom: check the
 * current state first — the event may already be in the channel's window —
 * then subscribe.
 * Resolves `undefined` on timeout or abort; events already at/behind
 * `sinceSeq` never match, so a stale pre-baseline publish cannot satisfy a
 * fresh wait.
 */
export function waitForChannelEvent<K extends GlobalAiChannelName>(
  channel: K,
  sinceSeq: number,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<ChannelEvent<GlobalAiChannelPayloads[K]> | undefined> {
  const firstSince = (channels: GlobalAiEventChannels) =>
    channelEventsSince(channels[channel], sinceSeq)[0];

  const immediate = firstSince(useGlobalAiCommandStore.getState().channels);
  if (immediate) return Promise.resolve(immediate);
  if (signal?.aborted) return Promise.resolve(undefined);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve(undefined);
    }, timeoutMs);
    const onAbort = () => {
      cleanup();
      resolve(undefined);
    };
    const cleanup = () => {
      clearTimeout(timer);
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort);
    const unsubscribe = useGlobalAiCommandStore.subscribe((state) => {
      const hit = firstSince(state.channels);
      if (hit) {
        cleanup();
        resolve(hit);
      }
    });
  });
}

/**
 * TODO(backend-adaptive): DELETE along with the refinement pass (see
 * `resolveThenEmitRouteFilters` in `assistant-engine-core.ts`). No other
 * caller exists — this helper was added specifically to detect
 * adaptive-taxonomy re-resolution after an `applyRouteFilters` command
 * lands. When the backend accepts `"adaptive"` directly, there's no
 * longer a pre-apply / post-apply taxonomy discrepancy to watch for.
 */
export function waitForTaxonomyChange(
  entityType: GlobalAssistantEntityType,
  baseline: AssistantTaxonomyPayload,
  timeoutMs: number
): Promise<AssistantTaxonomyPayload | null> {
  const current =
    useGlobalAiCommandStore.getState().activeTaxonomyByEntity[entityType];
  if (
    current &&
    (current.rolesTax !== baseline.rolesTax ||
      current.skillsTax !== baseline.skillsTax)
  ) {
    return Promise.resolve(current);
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    const unsubscribe = useGlobalAiCommandStore.subscribe((state) => {
      const next = state.activeTaxonomyByEntity[entityType];
      if (
        next &&
        (next.rolesTax !== baseline.rolesTax ||
          next.skillsTax !== baseline.skillsTax)
      ) {
        clearTimeout(timer);
        unsubscribe();
        resolve(next);
      }
    });
  });
}
